import { ElementInfo } from '../../../chemistry/ElementInfo';
import {
    isAromaticAtom, isAromaticBondOrder, isDelocalizedBondOrder,
} from '../../../util/chem_utils';
import {
    getMolAtomLines, removeCRs,
} from '../../../util/mol_format_utils';

function normalizeMolJSONElement(element) {
    if (element === '*') return element;
    return ElementInfo[element] ? element : '*';
}

function makeMolJSONAtomIdBuilder() {
    const counts = {};
    return (atom) => {
        const element = normalizeMolJSONElement(atom?.elem);
        counts[element] = (counts[element] || 0) + 1;
        return `${element}${counts[element]}`;
    };
}

function getMolJSONTargetAtoms(target) {
    const { compounds = [], atoms = [] } = target || {};
    const sourceAtoms = atoms.length > 0
        ? atoms
        : compounds.flatMap((compound) => compound.getAtoms());
    const seen = new Set();

    return sourceAtoms.filter((atom) => {
        if (!atom || seen.has(atom)) return false;
        seen.add(atom);
        if (atom.atom === 'LP') return false;
        return normalizeMolJSONElement(atom.elem) !== 'H';
    });
}

function getMolJSONBondOrder(bond) {
    const orderOriginal = Number(bond?.orderOriginal);
    if ([0, 1, 2, 3].includes(orderOriginal)) return orderOriginal;
    if (isAromaticBondOrder(orderOriginal) || isDelocalizedBondOrder(orderOriginal)) {
        return 1.5;
    }

    const kekuleOrder = Number(bond?.kekuleOrderOriginal);
    if ([1, 2].includes(kekuleOrder)) return kekuleOrder;

    const order = Number(bond?.order);
    if ([1, 2, 3].includes(order)) return order;
    return 1;
}

function getBondList(atom) {
    return atom?.getBonds?.() || atom?.bonds || [];
}

function parseMolAtomFormalCharge(atomLine) {
    const molChargeCode = Number(atomLine?.numbers?.slice(2, 5));
    return {
        0: 0,
        1: 3,
        2: 2,
        3: 1,
        5: -1,
        6: -2,
        7: -3,
    }[molChargeCode] || 0;
}

function parseMolblockFormalCharges(molblock) {
    const atomLines = getMolAtomLines(molblock);
    const formalCharges = atomLines.map(parseMolAtomFormalCharge);

    for (const line of removeCRs(molblock).split('\n')) {
        if (!line.startsWith('M  CHG')) continue;
        const parts = line.trim().split(/\s+/);
        const count = Number(parts[2]) || 0;
        for (let i = 0; i < count; i++) {
            const atomIndex = Number(parts[3 + i * 2]) - 1;
            const charge = Number(parts[4 + i * 2]) || 0;
            if (atomIndex >= 0) formalCharges[atomIndex] = charge;
        }
    }

    return formalCharges;
}

function getMolJSONCompoundChargeMap(compound) {
    const molblock = compound?.getMol2000?.();
    const atoms = compound?.getAtoms?.() || [];
    const atomFormalCharges = molblock ? parseMolblockFormalCharges(molblock) : [];
    const chargeMap = new Map();

    atoms.forEach((atom, index) => {
        const formalCharge = atomFormalCharges[index];
        if (formalCharge) chargeMap.set(atom, formalCharge);
    });
    return chargeMap;
}

function getMolJSONTargetCompounds(target) {
    const { compounds = [], atoms = [] } = target || {};
    if (compounds.length > 0) return compounds;

    const seen = new Set();
    return atoms
        .map((atom) => atom?.getAtomGroup?.())
        .filter((group) => {
            if (!group || seen.has(group) || typeof (group.getMol2000) !== 'function') return false;
            seen.add(group);
            return true;
        });
}

function getMolJSONChargeMap(target) {
    const chargeMap = new Map();
    for (const compound of getMolJSONTargetCompounds(target)) {
        for (const [atom, formalCharge] of getMolJSONCompoundChargeMap(compound).entries()) {
            chargeMap.set(atom, formalCharge);
        }
    }
    return chargeMap;
}

export function makeMolJSONData(target) {
    const atoms = getMolJSONTargetAtoms(target);
    if (atoms.length === 0) return null;

    const atomIds = new Map();
    const nextAtomId = makeMolJSONAtomIdBuilder();
    const moljsonAtoms = atoms.map((atom) => {
        const id = nextAtomId(atom);
        atomIds.set(atom, id);
        return {
            id,
            element: normalizeMolJSONElement(atom.elem),
        };
    });

    const atomSet = new Set(atoms);
    const moljsonBonds = [];
    const seenBonds = new Set();
    for (const atom of atoms) {
        for (const bond of getBondList(atom)) {
            if (seenBonds.has(bond)) continue;
            seenBonds.add(bond);
            const { atom1, atom2 } = bond;
            if (!atomSet.has(atom1) || !atomSet.has(atom2)) continue;
            moljsonBonds.push({
                source: atomIds.get(atom1),
                target: atomIds.get(atom2),
                order: getMolJSONBondOrder(bond),
            });
        }
    }

    const chargeMap = getMolJSONChargeMap(target);
    const charges = atoms
        .map((atom) => {
            const formalCharge = chargeMap.get(atom);
            return formalCharge
                ? { atom_id: atomIds.get(atom), formal_charge: formalCharge }
                : null;
        })
        .filter(Boolean);

    const aromaticNH = atoms
        .map((atom) => {
            if (normalizeMolJSONElement(atom.elem) !== 'N' || !isAromaticAtom(atom)) return null;
            const hcount = (atom.bondedAtoms || [])
                .filter((bondedAtom) => normalizeMolJSONElement(bondedAtom.elem) === 'H')
                .length;
            if (hcount < 1 || hcount > 2) return null;
            return { atom_id: atomIds.get(atom), hcount };
        })
        .filter(Boolean);

    return {
        atoms: moljsonAtoms,
        bonds: moljsonBonds,
        charges: charges.length > 0 ? charges : null,
        aromatic_n_h: aromaticNH.length > 0 ? aromaticNH : null,
    };
}
